import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE = "ec_i18n_en_v1";
const HAS_WORD = /[A-Za-zÀ-ú]{2,}/;
const ATTRS = ["placeholder", "title", "aria-label"];

const cache = (() => { try { return new Map(Object.entries(JSON.parse(localStorage.getItem(STORE) || "{}"))); } catch { return new Map(); } })();
const save = () => { try { localStorage.setItem(STORE, JSON.stringify(Object.fromEntries([...cache].slice(-3000)))); } catch { /* cheio */ } };
const applied = new WeakMap();
const enSet = new Set(cache.values());

// Grupo de texto: elemento cujos filhos são só texto (frase inteira de uma vez) ou o próprio nó de texto.
function unitOf(node) {
  const el = node.parentElement;
  if (!el || el.closest("script,style,[data-no-tr]")) return null;
  const kids = [...el.childNodes];
  const nodes = kids.every((k) => k.nodeType === 3) ? kids : [node];
  const pt = nodes.map((n) => n.nodeValue).join("").trim();
  if (!HAS_WORD.test(pt)) return null;
  return { nodes, pt };
}

function setUnit({ nodes }, en) {
  const lead = (nodes[0].nodeValue.match(/^\s*/) || [""])[0];
  const trail = (nodes[nodes.length - 1].nodeValue.match(/\s*$/) || [""])[0];
  nodes.forEach((n, i) => {
    const v = i === 0 ? lead + en + trail : "";
    if (n.nodeValue !== v) n.nodeValue = v;
    applied.set(n, v);
  });
  enSet.add(en);
}

// Página do cliente em inglês: traduz os textos visíveis (e os que vêm do servidor) com cache local e no servidor.
export function usePortalEnglish(token, on) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!on) { return undefined; }
    document.documentElement.lang = "en";
    let pending = new Map();
    let timer = null;
    let first = true;
    const flush = async () => {
      timer = null;
      const units = [...pending.values()];
      pending = new Map();
      const need = [...new Set(units.map((u) => u.pt).filter((t) => !cache.has(t)))];
      for (let i = 0; i < need.length; i += 150) {
        try {
          const { data } = await axios.post(`${API}/portal/${token}/i18n`, { texts: need.slice(i, i + 150) });
          Object.entries(data.map || {}).forEach(([k, v]) => { cache.set(k, v); enSet.add(v); });
        } catch { /* fica em português */ }
      }
      save();
      units.forEach((u) => { if (!u.attr && cache.has(u.pt)) setUnit(u, cache.get(u.pt)); });
      units.filter((u) => u.attr && cache.has(u.pt)).forEach((u) => { u.el.setAttribute(u.attr, cache.get(u.pt)); applied.set(u.el, cache.get(u.pt)); });
      if (first) { first = false; setReady(true); }
    };
    const queue = (u) => {
      if (!u.attr && cache.has(u.pt)) { setUnit(u, cache.get(u.pt)); return; }
      pending.set(u.attr ? `${u.attr}:${u.pt}:${pending.size}` : u.nodes[0], u);
      if (!timer) timer = setTimeout(flush, 120);
    };
    const visitText = (n) => {
      if (applied.get(n) === n.nodeValue) return;
      const u = unitOf(n);
      if (u && !enSet.has(u.pt)) queue(u);
    };
    const visitEl = (el) => {
      ATTRS.forEach((a) => {
        const v = el.getAttribute?.(a);
        if (v && HAS_WORD.test(v) && applied.get(el) !== v) {
          if (cache.has(v)) { el.setAttribute(a, cache.get(v)); applied.set(el, cache.get(v)); } else queue({ attr: a, el, pt: v });
        }
      });
    };
    const walk = (root) => {
      if (root.nodeType === 3) { visitText(root); return; }
      if (root.nodeType !== 1) return;
      visitEl(root);
      const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      for (let n = w.nextNode(); n; n = w.nextNode()) { if (n.nodeType === 3) visitText(n); else visitEl(n); }
    };
    walk(document.body);
    if (!pending.size) setReady(true);
    const obs = new MutationObserver((ms) => ms.forEach((m) => {
      if (m.type === "characterData") visitText(m.target);
      else if (m.type === "attributes") visitEl(m.target);
      else m.addedNodes.forEach(walk);
    }));
    obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    const fallback = setTimeout(() => setReady(true), 10000);
    return () => { obs.disconnect(); clearTimeout(fallback); if (timer) clearTimeout(timer); document.documentElement.lang = "pt-PT"; };
  }, [token, on]);
  return !on || ready;
}
